/**
 * Render loop. The loop only *redraws*; it never owns the remaining time.
 * requestAnimationFrame while visible, a 250 ms interval as fallback (rAF is
 * suspended in background tabs), and an immediate render whenever the page
 * becomes visible again so a throttled browser shows the correct time at once.
 */
export function startRenderLoop(render: (now: number) => void): () => void {
  let stopped = false;
  let rafId: number | null = null;

  const safeRender = () => {
    try {
      render(Date.now());
    } catch (err) {
      console.error('render failed', err);
    }
  };

  const tick = () => {
    if (stopped) return;
    safeRender();
    rafId = requestAnimationFrame(tick);
  };

  const interval = setInterval(safeRender, 250);
  const onVisible = () => {
    if (document.visibilityState === 'visible') safeRender();
  };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('pageshow', safeRender);
  window.addEventListener('focus', safeRender);

  safeRender();
  rafId = requestAnimationFrame(tick);

  return () => {
    stopped = true;
    clearInterval(interval);
    if (rafId !== null) cancelAnimationFrame(rafId);
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('pageshow', safeRender);
    window.removeEventListener('focus', safeRender);
  };
}
